import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { groupBy } from 'lodash'

import { TableMenu } from '../partnerModuleMenu/PartnerModuleMenu'

import { ErrorView, Loader } from '@percent/admin-dashboard/common/components'
import { PartnerScope } from '@percent/admin-dashboard/api/types'
import styles from './PartnerModuleListTable.module.scss'
import { ModuleOrder, ModuleType, rowModuleInfo } from './rowModuleInfo'
import { Typography } from '@percent/lemonade'
import { usePermissions } from '@percent/admin-dashboard/common/hooks'

const mapPartnersScopesToAllScopes = (data: PartnerScope[], moduleInfo: typeof rowModuleInfo) =>
  moduleInfo.map(a => ({
    ...a,
    active: data?.some(({ scopeName }) => scopeName === a.name)
  }))

export function PartnerModuleListTable({
  partnerId,
  data,
  isLoading,
  errorMessage,
  refreshApi
}: {
  partnerId: string
  data: any
  isLoading: boolean
  errorMessage: string
  refreshApi: VoidFunction
}) {
  const [scopeSorted, setScopeSorted] = useState<typeof rowModuleInfo>([])
  const { t } = useTranslation()
  const { userHasPermissions } = usePermissions()

  useEffect(() => {
    if (!isLoading && data) {
      setScopeSorted(mapPartnersScopesToAllScopes(data as PartnerScope[], rowModuleInfo))
    }
  }, [data, isLoading])

  if (isLoading) {
    return <Loader />
  }

  if (!data && errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  const groupedAndSortedModules = Object.entries(groupBy(scopeSorted, 'type')).sort(
    (a, b) => ModuleOrder[a[0] as ModuleType] - ModuleOrder[b[0] as ModuleType]
  )

  const getModuleTitle = (type: ModuleType) => {
    switch (type) {
      case ModuleType.DISBURSEMENTS:
        return t('typography.disbursements')
      case ModuleType.GRANTS:
        return t('typography.grants')
      case ModuleType.WORKPLACE_GIVING:
        return t('typography.workplaceGiving')
      case ModuleType.VERIFICATIONS_INDIVIDUAL_API:
        return t('typography.verificationsIndividualAPI')
      case ModuleType.VALIDATION_SUBMISSION:
        return t('typography.validationSubmission')
      case ModuleType.SSO_PROVISIONING:
        return t('typography.sso&provisioning')
      case ModuleType.ANALYTICS:
        return t('typography.analytics')
      default:
        return t('typography.other')
    }
  }

  const permission = (action: string) => userHasPermissions(`partners:${action}`)

  return (
    <TableContainer className={styles.tableContainer}>
      {groupedAndSortedModules.map(([type, modules]) => (
        <div key={type}>
          <Typography variant="h4" style={{ marginBottom: '16px' }}>
            {getModuleTitle(type as ModuleType)}
          </Typography>
          <Table className={styles.table}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="default-bold">{t('typography.modules')}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="default-bold">{t('typography.status')}</Typography>
                </TableCell>
                {permission('can_set_scopes') && <TableCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {modules?.map(module => (
                <TableRow key={`partner-module-${module.name}`}>
                  <TableCell className={styles.moduleDescriptionCell}>
                    {module.logoComponent}
                    <p className={styles.moduleTitle} data-testid={module.name}>
                      {module.title}
                    </p>
                  </TableCell>
                  <TableCell className={styles.logoTableCell}>
                    <p>{module.active ? t('typography.active') : t('typography.inactive')}</p>
                  </TableCell>
                  {permission('can_set_scopes') && (
                    <TableCell
                      className={styles.statusSwitchTableCell}
                      data-testid={`partner-module-menu-${module.name}`}
                    >
                      <TableMenu
                        refresh={refreshApi}
                        moduleName={module.name}
                        partnerId={partnerId}
                        moduleTitle={module.title}
                        isModuleActive={module.active}
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ))}
    </TableContainer>
  )
}
